import { type components, unwrap } from '@shared/api-client/index';
import { toastApiError } from '@shared/ui/toast/toast-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '../core/api';

export type Scope = components['schemas']['ScopeResponse'];
export type ScopeShape = NonNullable<Scope['shape']>;

export const scopesKey = ['scopes'] as const;

/**
 * The workspace's scopes (client-side); the list omits sections/questions.
 * TanStack Query v5 dropped `onError` from `useQuery` itself, so a failed read
 * toasts via this effect (the equivalent of the old callback) — the page still
 * reads `isLoading`/`isError` off the same query to render its own loading/error
 * state; the toast is just the ambient "something went wrong" nudge.
 */
export function useScopes() {
	const query = useQuery<Scope[]>({
		queryKey: scopesKey,
		// Include archived scopes so the list can offer an "Archived" view; the page
		// hides them from the normal filters and surfaces them under their own tab.
		queryFn: async () =>
			unwrap(await api.GET('/v1/scopes', { params: { query: { archived: true } } })),
		// The list paints from a persisted cache; a returning user (an agent may have
		// advanced a scope while they were away) would otherwise see a stale row until
		// something changes. Always revalidate on mount — scoped to scope queries so the
		// shared provider's default (Tasks/Help) is untouched (PLAN-88).
		refetchOnMount: 'always',
	});

	useEffect(() => {
		if (query.isError) {
			toastApiError(query.error);
		}
	}, [query.isError, query.error]);

	return query;
}

/** Archive or unarchive a scope — optimistically flips the row, rolls back on error. */
export function useArchiveScope() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			id,
			archived,
			expectedVersion,
		}: {
			id: string;
			archived: boolean;
			expectedVersion?: number;
		}) =>
			unwrap(
				await api.PATCH('/v1/scopes/{id}', {
					params: { path: { id } },
					body: { archived, expectedVersion },
				}),
			),
		onMutate: async ({ id, archived }) => {
			await queryClient.cancelQueries({ queryKey: scopesKey });
			const previous = queryClient.getQueryData<Scope[]>(scopesKey);
			queryClient.setQueryData<Scope[]>(scopesKey, (prev) =>
				prev?.map((scope) => (scope.id === id ? { ...scope, archived } : scope)),
			);
			return { previous };
		},
		onError: (error, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(scopesKey, context.previous);
			}
			toastApiError(error);
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey: scopesKey }),
	});
}

/** Delete a scope (soft-delete/tombstone) — optimistically drops the row. */
export function useDeleteScope() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) =>
			unwrap(await api.DELETE('/v1/scopes/{id}', { params: { path: { id } } })),
		onMutate: async (id) => {
			await queryClient.cancelQueries({ queryKey: scopesKey });
			const previous = queryClient.getQueryData<Scope[]>(scopesKey);
			queryClient.setQueryData<Scope[]>(scopesKey, (prev) =>
				prev?.filter((scope) => scope.id !== id),
			);
			return { previous };
		},
		onError: (error, _id, context) => {
			if (context?.previous) {
				queryClient.setQueryData(scopesKey, context.previous);
			}
			toastApiError(error);
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey: scopesKey }),
	});
}

export interface CreateScopeInput {
	/** Omitted for the v6 flow (PLAN-128): a new scope is created unnamed and the AI
	 *  names it from the Describe conversation. */
	title?: string;
	shape?: ScopeShape;
}

/**
 * Start a new scope: creates a DRAFT and refreshes the list so the new row appears.
 * The v6 flow creates it without a title (PLAN-128) and drops straight onto Describe,
 * where the AI names it; the caller navigates to the returned scope.
 */
export function useCreateScope() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (input: CreateScopeInput | void) =>
			unwrap(
				await api.POST('/v1/scopes', {
					body: (input ?? {}) as components['schemas']['CreateScopeRequest'],
				}),
			),
		onError: toastApiError,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: scopesKey }),
	});
}
